/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AppLocaleFull } from '../models/AppLocaleFull';
import type { BuildFormResponse } from '../models/BuildFormResponse';
import type { Collection } from '../models/Collection';
import type { ContentDeliveryList } from '../models/ContentDeliveryList';
import type { ContentListResponse } from '../models/ContentListResponse';
import type { ContentSelectInstance } from '../models/ContentSelectInstance';
import type { ContentTypeCreate } from '../models/ContentTypeCreate';
import type { ContentTypeEdit } from '../models/ContentTypeEdit';
import type { ContentTypeFullResponse } from '../models/ContentTypeFullResponse';
import type { CountContentsOfType } from '../models/CountContentsOfType';
import type { CreateContent } from '../models/CreateContent';
import type { GetSlug } from '../models/GetSlug';
import type { Mapping } from '../models/Mapping';
import type { PaginatedContentTypeFullResponseList } from '../models/PaginatedContentTypeFullResponseList';
import type { PaginatedContentTypeShortList } from '../models/PaginatedContentTypeShortList';
import type { PaginatedContentTypeWithFormResponseList } from '../models/PaginatedContentTypeWithFormResponseList';
import type { PaginatedDetailsList } from '../models/PaginatedDetailsList';
import type { SuccessOperation } from '../models/SuccessOperation';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ContentTypeService {

  /**
   * @param id 
   * @returns BuildFormResponse 
   * @throws ApiError
   */
  public static contentTypeBuildFormList(
id: string,
): CancelablePromise<Array<BuildFormResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/build-form/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @returns SuccessOperation 
   * @throws ApiError
   */
  public static contentTypeClearCacheRetrieve(): CancelablePromise<SuccessOperation> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/clear-cache',
    });
  }

  /**
   * @param category * `collection` - collection
 * * `component` - component
 * * `page_type` - page_type
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedDetailsList 
   * @throws ApiError
   */
  public static contentTypeComponentsList(
category?: 'collection' | 'component' | 'page_type',
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedDetailsList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/components/',
      query: {
        'category': category,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param id 
   * @param requestBody 
   * @returns Collection 
   * @throws ApiError
   */
  public static contentTypeCopyCreate(
id: string,
requestBody?: ContentTypeCreate,
): CancelablePromise<Collection> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/content-type/copy/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id 
   * @returns CountContentsOfType 
   * @throws ApiError
   */
  public static contentTypeCountRetrieve(
id: string,
): CancelablePromise<CountContentsOfType> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/count/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns Collection 
   * @throws ApiError
   */
  public static contentTypeCreateCreate(
requestBody?: ContentTypeCreate,
): CancelablePromise<Collection> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/content-type/create/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id 
   * @returns void 
   * @throws ApiError
   */
  public static contentTypeDeleteDestroy(
id: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/content-type/delete/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param apiName 
   * @param contentId 
   * @param depth 
   * @returns any No response body
   * @throws ApiError
   */
  public static contentTypeDeliveryByIdRetrieve(
apiName: string,
contentId: string,
depth?: number,
): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/delivery/{api_name}/by-id/{content_id}/',
      path: {
        'api_name': apiName,
        'content_id': contentId,
      },
      query: {
        'depth': depth,
      },
    });
  }

  /**
   * @param apiName 
   * @param slug 
   * @param depth 
   * @param fields 
   * @param filter 
   * @param search 
   * @returns any No response body
   * @throws ApiError
   */
  public static contentTypeDeliveryBySlugRetrieve(
apiName: string,
slug: string,
depth?: number,
fields?: Array<string>,
filter?: Record<string, any>,
search?: string,
): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/delivery/{api_name}/by-slug/{slug}/',
      path: {
        'api_name': apiName,
        'slug': slug,
      },
      query: {
        'depth': depth,
        'fields': fields,
        'filter': filter,
        'search': search,
      },
    });
  }

  /**
   * @param apiName 
   * @param key 
   * @param value 
   * @returns any No response body
   * @throws ApiError
   */
  public static contentTypeDeliveryGroupRetrieve(
apiName: string,
key: string,
value: string,
): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/delivery/{api_name}/group/',
      path: {
        'api_name': apiName,
      },
      query: {
        'key': key,
        'value': value,
      },
    });
  }

  /**
   * @param apiName 
   * @param depth 
   * @param fields 
   * @param filter 
   * @param search 
   * @param sort 
   * @returns ContentDeliveryList 
   * @throws ApiError
   */
  public static contentTypeDeliveryListRetrieve(
apiName: string,
depth?: number,
fields?: Array<string>,
filter?: Record<string, any>,
search?: string,
sort?: string,
): CancelablePromise<ContentDeliveryList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/delivery/{api_name}/list/',
      path: {
        'api_name': apiName,
      },
      query: {
        'depth': depth,
        'fields': fields,
        'filter': filter,
        'search': search,
        'sort': sort,
      },
    });
  }

  /**
   * @returns AppLocaleFull 
   * @throws ApiError
   */
  public static contentTypeDeliveryLocalesRetrieve(): CancelablePromise<AppLocaleFull> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/delivery/locales/',
    });
  }

  /**
   * @param requestBody 
   * @returns ContentDeliveryList 
   * @throws ApiError
   */
  public static contentTypeDeliveryPipeCreate(
requestBody?: ContentDeliveryList,
): CancelablePromise<ContentDeliveryList> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/content-type/delivery/pipe/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Public version of ListContentInstancesView that doesn't require authentication.
 * Useful for public content delivery and frontend applications.
   * @param filterRepeaterContent Filter repeater field contents to only show items matching search criteria
   * @param page 
   * @param perPage 
   * @param search 
   * @param sortBy 
   * @param status 
   * @param typeId 
   * @param typeIds 
   * @param years 
   * @returns ContentListResponse 
   * @throws ApiError
   */
  public static contentTypeDeliveryPublicListRetrieve(
filterRepeaterContent: boolean = false,
page: number = 1,
perPage: number = 50,
search?: string,
sortBy?: string,
status?: string,
typeId?: string,
typeIds?: string,
years?: string,
): CancelablePromise<ContentListResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/delivery/public-list/',
      query: {
        'filter_repeater_content': filterRepeaterContent,
        'page': page,
        'per_page': perPage,
        'search': search,
        'sort_by': sortBy,
        'status': status,
        'type_id': typeId,
        'type_ids': typeIds,
        'years': years,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns GetSlug 
   * @throws ApiError
   */
  public static contentTypeDeliverySlugLookupCreate(
requestBody: Array<GetSlug>,
): CancelablePromise<Array<GetSlug>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/content-type/delivery/slug-lookup/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id 
   * @param requestBody 
   * @returns Collection 
   * @throws ApiError
   */
  public static contentTypeEditUpdate(
id: string,
requestBody?: ContentTypeEdit,
): CancelablePromise<Collection> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/content-type/edit/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param category * `collection` - collection
 * * `component` - component
 * * `page_type` - page_type
   * @param idsIn 
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search 
   * @param status * `draft` - draft
 * * `published` - published
   * @returns PaginatedContentTypeWithFormResponseList 
   * @throws ApiError
   */
  public static contentTypeFormsListList(
category?: 'collection' | 'component' | 'page_type',
idsIn?: Array<string>,
page?: number,
pageSize?: number,
search?: string,
status?: 'draft' | 'published',
): CancelablePromise<PaginatedContentTypeWithFormResponseList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/forms-list/',
      query: {
        'category': category,
        'ids__in': idsIn,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'status': status,
      },
    });
  }

  /**
   * @param category * `collection` - collection
 * * `component` - component
 * * `page_type` - page_type
   * @param idsIn 
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search 
   * @param status * `draft` - draft
 * * `published` - published
   * @returns PaginatedContentTypeFullResponseList 
   * @throws ApiError
   */
  public static contentTypeListList(
category?: 'collection' | 'component' | 'page_type',
idsIn?: Array<string>,
page?: number,
pageSize?: number,
search?: string,
status?: 'draft' | 'published',
): CancelablePromise<PaginatedContentTypeFullResponseList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/list/',
      query: {
        'category': category,
        'ids__in': idsIn,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'status': status,
      },
    });
  }

  /**
   * @param id 
   * @param requestBody 
   * @returns Collection 
   * @throws ApiError
   */
  public static contentTypeMappingsUpdate(
id: string,
requestBody: Array<Mapping>,
): CancelablePromise<Collection> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/content-type/mappings/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param typeId 
   * @param requestBody 
   * @returns CreateContent 
   * @throws ApiError
   */
  public static contentTypeModelCrudCreate(
typeId: string,
requestBody: CreateContent,
): CancelablePromise<CreateContent> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/content-type/model/crud/{type_id}/',
      path: {
        'type_id': typeId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id 
   * @param typeId 
   * @returns CreateContent 
   * @throws ApiError
   */
  public static contentTypeModelCrudRetrieve(
id: string,
typeId: string,
): CancelablePromise<CreateContent> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/model/crud/{type_id}/{id}/',
      path: {
        'id': id,
        'type_id': typeId,
      },
    });
  }

  /**
   * @param id 
   * @param typeId 
   * @param requestBody 
   * @returns CreateContent 
   * @throws ApiError
   */
  public static contentTypeModelCrudUpdate(
id: string,
typeId: string,
requestBody: CreateContent,
): CancelablePromise<CreateContent> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/content-type/model/crud/{type_id}/{id}/',
      path: {
        'id': id,
        'type_id': typeId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id 
   * @param typeId 
   * @returns void 
   * @throws ApiError
   */
  public static contentTypeModelCrudDestroy(
id: string,
typeId: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/content-type/model/crud/{type_id}/{id}/',
      path: {
        'id': id,
        'type_id': typeId,
      },
    });
  }

  /**
   * @param id 
   * @param typeId 
   * @returns CreateContent 
   * @throws ApiError
   */
  public static contentTypeModelDuplicateCreate(
id: string,
typeId: string,
): CancelablePromise<CreateContent> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/content-type/model/duplicate/{type_id}/{id}/',
      path: {
        'id': id,
        'type_id': typeId,
      },
    });
  }

  /**
   * @param filterRepeaterContent Filter repeater field contents to only show items matching search criteria
   * @param page 
   * @param perPage 
   * @param search 
   * @param sortBy 
   * @param status 
   * @param typeId 
   * @param typeIds 
   * @param years 
   * @returns ContentListResponse 
   * @throws ApiError
   */
  public static contentTypeModelListRetrieve(
filterRepeaterContent: boolean = false,
page: number = 1,
perPage: number = 50,
search?: string,
sortBy?: string,
status?: string,
typeId?: string,
typeIds?: string,
years?: string,
): CancelablePromise<ContentListResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/model/list/',
      query: {
        'filter_repeater_content': filterRepeaterContent,
        'page': page,
        'per_page': perPage,
        'search': search,
        'sort_by': sortBy,
        'status': status,
        'type_id': typeId,
        'type_ids': typeIds,
        'years': years,
      },
    });
  }

  /**
   * @param typeId 
   * @param include 
   * @param search 
   * @returns ContentSelectInstance 
   * @throws ApiError
   */
  public static contentTypeModelSelectRetrieve(
typeId: string,
include?: Array<string>,
search?: string,
): CancelablePromise<ContentSelectInstance> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/model/select/',
      query: {
        'include': include,
        'search': search,
        'type_id': typeId,
      },
    });
  }

  /**
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedContentTypeShortList 
   * @throws ApiError
   */
  public static contentTypePinedList(
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedContentTypeShortList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/pined/',
      query: {
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param id 
   * @returns ContentTypeFullResponse 
   * @throws ApiError
   */
  public static contentTypeRetrieveRetrieve(
id: string,
): CancelablePromise<ContentTypeFullResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/content-type/retrieve/{id}/',
      path: {
        'id': id,
      },
    });
  }

}
